import styled from "styled-components";
import { motion } from "framer-motion";

export const ProgressContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin: 0.5rem 0 1.2rem 0;
  padding: 0 0.5rem 0 0.5rem;
  position: relative;
  overflow-x: auto;
  border-bottom: none;
  background: transparent;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) =>
    `${theme.colors.border} ${theme.colors.background}`};
  gap: 1rem;

  @media (max-width: 600px) {
    flex-wrap: nowrap;
    gap: 0.9rem;
    margin: 0.5rem 0 1rem 0;
  }

  @media (max-width: 390px) {
    gap: 0.25rem;
    padding: 0 0.25rem 0 0.25rem;
  }
`;

export const ProgressLine = styled.div<{
  $isActive: boolean;
  $progress?: number;
}>`
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  height: 2px;
  background: ${({ theme }) => theme.colors.gradientLight[0]};
  z-index: 1;
  border-radius: 1px;
  pointer-events: none;

  @media (max-width: 600px) {
    top: 19px;
    left: 18px;
    right: 18px;
  }

  @media (max-width: 390px) {
    top: 17px;
    left: 16px;
    right: 16px;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ $progress }) => ($progress ? `${$progress}%` : "0%")};
    background: ${({ theme, $isActive }) =>
      $isActive ? theme.colors.primary : "transparent"};
    border-radius: 1px;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

export const ProgressStep = styled(motion.div)<{ $isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
  min-width: 60px;
  background: transparent;
  margin-top: 0;

  @media (max-width: 600px) {
    min-width: 64px;
    gap: 0.35rem;
  }

  @media (max-width: 390px) {
    min-width: 56px;
    gap: 0.25rem;
  }

  /* Add a white circle behind the step number to create the "line through" effect */
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: white;
    z-index: 1;

    @media (max-width: 600px) {
      width: 40px;
      height: 40px;
    }

    @media (max-width: 390px) {
      width: 34px;
      height: 34px;
    }
  }
`;

export const StepNumber = styled.div<{ $isActive: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.gradientLight[0]};
  color: ${({ theme, $isActive }) =>
    $isActive ? "white" : theme.colors.secondaryText};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  box-shadow: none;
  position: relative;
  z-index: 2;

  @media (max-width: 600px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }

  @media (max-width: 390px) {
    width: 34px;
    height: 34px;
    font-size: 0.9rem;
  }
`;

export const StepLabel = styled.span<{ $isActive: boolean }>`
  font-size: 0.6rem;
  font-weight: 600;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.secondaryText};
  margin-top: 0.2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
  hyphens: auto;
  max-width: 100%;

  @media (max-width: 600px) {
    font-size: 0.8rem;
    margin-top: 0.2rem;
  }

  @media (max-width: 390px) {
    font-size: 0.68rem;
    margin-top: 0.1rem;
    line-height: 1.1;
    word-break: break-word;
    overflow-wrap: break-word;
  }
`;

export const NavigationSection = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

export const NavigationButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  width: 100%;
`;
